/**
 * RoleCard Component
 *
 * Displays a single role's information in a card format.
 */

import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Stack,
  Box,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import { usePermissions, type Role } from '@/hooks';
import { useAuthStore } from '@/stores/authStore';

const MANAGE_ROLES_PERMISSION = 'settings.manage_roles';

interface RoleCardProps {
  role: Role;
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
}

export function RoleCard({ role, onEdit, onDelete }: RoleCardProps) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Edit and delete require settings.manage_roles server-side; hide what would 403.
  const canManageRoles = hasPermission(MANAGE_ROLES_PERMISSION);
  const { data: permissions = [] } = usePermissions();

  const permissionCount = role.permissionIds.length;
  const totalPermissions = permissions.length;

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        '&:hover': {
          boxShadow: 4,
        },
      }}
    >
      <CardContent sx={{ flex: 1 }}>
        <Stack spacing={2}>
          {/* Header with role name and actions */}
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Typography variant="h6" component="div" gutterBottom>
                {role.name}
              </Typography>
              {role.isSystem && (
                <Chip
                  label={t('rolesManagement.systemRole')}
                  size="small"
                  color="primary"
                  variant="outlined"
                  icon={<LockIcon />}
                  sx={{ mb: 1 }}
                />
              )}
            </Box>
            {canManageRoles && (
              <Stack direction="row" spacing={0.5}>
                <Tooltip title={tc('edit')}>
                  <IconButton
                    size="small"
                    onClick={() => onEdit(role)}
                    aria-label={t('rolesManagement.editRole')}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!role.isSystem && (
                  <Tooltip title={tc('delete')}>
                    <IconButton
                      size="small"
                      onClick={() => onDelete(role)}
                      aria-label={t('rolesManagement.deleteRole')}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Box>

          {/* Description */}
          <Typography variant="body2" color="text.secondary">
            {role.description}
          </Typography>

          {/* Statistics */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Stack direction="row" spacing={0.5} alignItems="center">
              <PeopleIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {t('rolesManagement.userCount', { count: role.userCount ?? 0 })}
              </Typography>
            </Stack>

            <Typography variant="body2" color="text.secondary">
              &bull;
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {t('rolesManagement.permissionCount', { count: permissionCount, total: totalPermissions })}
            </Typography>
          </Stack>

          {/* Permission percentage */}
          <Box>
            <Box
              sx={{
                width: '100%',
                height: 4,
                backgroundColor: 'grey.200',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: totalPermissions > 0 ? `${(permissionCount / totalPermissions) * 100}%` : 0,
                  height: '100%',
                  backgroundColor: 'primary.main',
                  transition: 'width 0.3s ease',
                }}
              />
            </Box>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}
